import React, { useEffect, useMemo, useState } from 'react';
import { getAuthHeader, getUserInfo } from '../utils/tokenManager';

const ClientManagement = () => {
  const sampleInfluencers = useMemo(() => {
    return [
      {
        id: 'inf-001',
        name: '김하늘',
        handle: '@haneul_travel',
        platforms: ['INSTAGRAM', 'YOUTUBE'],
        followers: { instagram: 128000, youtube: 35600 },
        categories: ['여행', '호텔', '맛집'],
        location: '서울',
        bio: '국내/해외 여행 콘텐츠를 제작합니다. 호텔 리뷰/브이로그 위주로 진행해요.',
        contactEmail: 'haneul@example.com',
      },
      {
        id: 'inf-002',
        name: '박준호',
        handle: '@junho_daily',
        platforms: ['INSTAGRAM', 'BLOG'],
        followers: { instagram: 54000, blog: 22000 },
        categories: ['라이프스타일', '카페', '전시'],
        location: '부산',
        bio: '일상 기반 리뷰를 빠르게 제작합니다. 사진/릴스 모두 가능합니다.',
        contactEmail: 'junho@example.com',
      },
      {
        id: 'inf-003',
        name: '이서연',
        handle: '@seoyeon_foodtrip',
        platforms: ['YOUTUBE'],
        followers: { youtube: 91000 },
        categories: ['맛집', '미식', '여행'],
        location: '대구',
        bio: '먹방/맛집 탐방을 중심으로 여행 콘텐츠를 운영합니다.',
        contactEmail: 'seoyeon@example.com',
      },
    ];
  }, []);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [selectedInfluencer, setSelectedInfluencer] = useState(null);

  const [filterName, setFilterName] = useState('');
  const [filterManager, setFilterManager] = useState('');
  const [filterPhone, setFilterPhone] = useState('');
  const [filterEmail, setFilterEmail] = useState('');

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [editingClient, setEditingClient] = useState(null);
  const [newClientName, setNewClientName] = useState('');
  const [newClientEmail, setNewClientEmail] = useState('');
  const [newClientOwner, setNewClientOwner] = useState('');
  const [newClientPhone, setNewClientPhone] = useState('');
  const [newClientAddress, setNewClientAddress] = useState('');
  const [newClientActive, setNewClientActive] = useState(true);
  const [activeTab, setActiveTab] = useState('active'); // 'active' | 'inactive'

  const fetchClientList = async () => {
    const { companyNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@업체번호', 값: String(companyNo ?? '') },
        { 컬럼명: '@사용여부1', 값: false },
        { 컬럼명: '@사용여부2', 값: true },
      ],
    };
    const url = 'https://trpliveapi1.tripbox.co.kr/ERP/GetDataSet?procedureName=DBO.CW_S_%ED%8C%90%EB%A7%A4%EA%B1%B0%EB%9E%98%EC%B2%98';
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error('Failed to load');
    const data = await res.json();
    return Array.isArray(data?.table) ? data.table : [];
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');
      const list = await fetchClientList();
      setRows(list);
    } catch (e) {
      setError('데이터를 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const resolveCreatedClientNoFromList = (list, { name, email }) => {
    const normalizedName = String(name || '').trim();
    const normalizedEmail = String(email || '').trim();
    if (!normalizedName) return null;
    if (!normalizedEmail) return null;

    const candidates = (Array.isArray(list) ? list : []).filter((r) => {
      const rName = String(r?.거래처명 || '').trim();
      const rEmail = String(r?.대표이메일 || '').trim();
      return rName === normalizedName && rEmail === normalizedEmail;
    });
    if (candidates.length === 0) return null;
    candidates.sort((a, b) => Number(b?.거래처번호 || 0) - Number(a?.거래처번호 || 0));
    const picked = candidates[0];
    const no = Number(picked?.거래처번호);
    return Number.isFinite(no) ? no : null;
  };

  const createClientStaff = async ({ clientNo, loginId, nameKr, phone }) => {
    const safeClientNo = Number(clientNo);
    if (!Number.isFinite(safeClientNo)) {
      throw new Error('거래처번호를 확인할 수 없습니다.');
    }

    const normalizedLoginId = String(loginId || '').trim();
    const normalizedNameKr = String(nameKr || '').trim();
    const normalizedPhone = String(phone || '').trim();

    if (!normalizedLoginId) {
      throw new Error('대표이메일이 없어 직원 아이디를 생성할 수 없습니다.');
    }
    if (!normalizedNameKr) {
      throw new Error('대표자명이 없어 직원 이름을 생성할 수 없습니다.');
    }

    const { companyNo, userNo } = getUserInfo() || {};
    const payload = {
      Table1: [
        { 컬럼명: '@등록자', 값: String(userNo ?? '') },
        { 컬럼명: '@업체번호', 값: String(companyNo ?? '') },
        { 컬럼명: '@거래처번호', 값: safeClientNo },
        { 컬럼명: '@사원번호', 값: 'NULL' },
        { 컬럼명: '@모드', 값: '신규' },
        { 컬럼명: '@아이디', 값: normalizedLoginId },
        { 컬럼명: '@이름', 값: normalizedNameKr },
        { 컬럼명: '@국가번호', 값: '' },
        { 컬럼명: '@휴대폰', 값: normalizedPhone },
        { 컬럼명: '@관리자여부', 값: true },
        { 컬럼명: '@사용여부', 값: true },
        { 컬럼명: '@알림수신', 값: false },
      ],
    };

    const url = 'https://trpliveapi1.tripbox.co.kr/ERP/GetDataSet?procedureName=DBO.CW_T_%ED%8C%90%EB%A7%A4%EC%B2%98%EC%A7%81%EC%9B%90';
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      throw new Error('거래처 직원 자동 생성에 실패했습니다.');
    }
    const data = await res.json().catch(() => null);
    const message = data?.table?.[0]?.message ?? data?.table?.[0]?.Message ?? data?.message ?? null;
    if (message) {
      const msg = String(message);
      if (msg) throw new Error(msg);
    }
    return data;
  };

  const handleUpdateClient = async (active) => {
    if (!editingClient) return;
    if (!newClientName) {
      setCreateError('거래처명을 입력해주세요.');
      return;
    }
    try {
      setCreating(true);
      setCreateError('');
      setNewClientActive(!!active);
      const { companyNo, userNo } = getUserInfo() || {};
      const payload = {
        Table1: [
          { 컬럼명: '@공급업체번호', 값: String(companyNo ?? '') },
          { 컬럼명: '@등록자', 값: String(userNo ?? '') },
          { 컬럼명: '@거래처번호', 값: Number(editingClient?.거래처번호) },
          { 컬럼명: '@신규업체명', 값: newClientName },
          { 컬럼명: '@이메일', 값: newClientEmail || '' },
          { 컬럼명: '@대표자', 값: newClientOwner || '' },
          { 컬럼명: '@담당자', 값: '' },
          { 컬럼명: '@기본주소', 값: newClientAddress || '' },
          { 컬럼명: '@상세주소', 값: '' },
          { 컬럼명: '@우편번호', 값: '' },
          { 컬럼명: '@국가번호', 값: '' },
          { 컬럼명: '@대표전화', 값: newClientPhone || '0' },
          { 컬럼명: '@사용여부', 값: !!active },
          { 컬럼명: '@은행코드', 값: '      ' },
          { 컬럼명: '@계좌번호', 값: '' },
          { 컬럼명: '@예금주', 값: '' },
          { 컬럼명: '@은행명', 값: '' },
          { 컬럼명: '@비고', 값: '' },
          { 컬럼명: '@거래처구분코드', 값: '      ' },
          { 컬럼명: '@첨부파일리스트', 값: '[]' },
          { 컬럼명: '@거래처키워드', 값: '' },
          { 컬럼명: '@미수유예일', 값: 0 },
        ],
      };
      const url = 'https://trpliveapi1.tripbox.co.kr/ERP/GetDataSet?procedureName=DBO.TRP_T_%ED%8C%90%EB%A7%A4%EA%B1%B0%EB%9E%98%EC%B2%98%EC%A0%95%EB%B3%B4%EC%88%98%EC%A0%95';
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Failed to update');
      await res.json();
      setIsCreateModalOpen(false);
      setEditingClient(null);
      await fetchData();
    } catch (e) {
      setCreateError('거래처를 수정하지 못했습니다.');
    } finally {
      setCreating(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const isEditMode = !!editingClient;

  const handleOpenCreateModal = () => {
    setCreateError('');
    setEditingClient(null);
    setNewClientName('');
    setNewClientEmail('');
    setNewClientOwner('');
    setNewClientPhone('');
    setNewClientAddress('');
    setNewClientActive(true);
    setIsCreateModalOpen(true);
  };

  const handleCloseCreateModal = () => {
    if (creating) return;
    setIsCreateModalOpen(false);
  };

  const handleOpenEditModal = (client) => {
    setCreateError('');
    setEditingClient(client);
    setNewClientName(client?.거래처명 || '');
    setNewClientEmail(client?.대표이메일 || '');
    setNewClientOwner(client?.대표자명 || '');
    setNewClientPhone(client?.대표전화 || '');
    setNewClientAddress(client?.사업장주소 || '');
    setNewClientActive(!!client?.사용여부);
    setIsCreateModalOpen(true);
  };

  const handleCreateClient = async () => {
    if (!newClientName) {
      setCreateError('거래처명을 입력해주세요.');
      return;
    }

    if (!String(newClientEmail || '').trim() || !String(newClientOwner || '').trim() || !String(newClientPhone || '').trim()) {
      setCreateError('대표이메일, 대표자명, 대표전화를 입력해주세요.');
      return;
    }
    try {
      setCreating(true);
      setCreateError('');
      const { companyNo, userNo } = getUserInfo() || {};
      const payload = {
        Table1: [
          { 컬럼명: '@공급업체번호', 값: String(companyNo ?? '') },
          { 컬럼명: '@등록자', 값: String(userNo ?? '') },
          { 컬럼명: '@업체번호', 값: 'NULL' },
          { 컬럼명: '@등록기준', 값: '0' },
          { 컬럼명: '@신규업체명', 값: newClientName },
          { 컬럼명: '@아이디', 값: '' },
          { 컬럼명: '@이메일', 값: newClientEmail || '' },
          { 컬럼명: '@대표자', 값: newClientOwner || '' },
          { 컬럼명: '@담당자', 값: '' },
          { 컬럼명: '@기본주소', 값: newClientAddress || '' },
          { 컬럼명: '@상세주소', 값: '' },
          { 컬럼명: '@우편번호', 값: '' },
          { 컬럼명: '@국가번호', 값: '' },
          { 컬럼명: '@대표전화', 값: newClientPhone || '0' },
          { 컬럼명: '@이메일계정모름', 값:true},
        ],
      };
      const url = 'https://trpliveapi1.tripbox.co.kr/ERP/GetDataSet?procedureName=DBO.CW_T_%ED%8C%90%EB%A7%A4%EA%B1%B0%EB%9E%98%EC%B2%98%EC%B6%94%EA%B0%80_01';
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Failed to create');
      const saveData = await res.json().catch(() => null);

      const createdClientNoRaw = saveData?.table?.[0]?.거래처번호 ?? saveData?.table?.[0]?.거래처No ?? saveData?.table?.[0]?.id ?? null;
      const createdClientNoParsed = createdClientNoRaw != null ? Number(createdClientNoRaw) : NaN;
      let createdClientNo = Number.isFinite(createdClientNoParsed) ? createdClientNoParsed : null;

      if (createdClientNo == null) {
        try {
          const list = await fetchClientList();
          createdClientNo = resolveCreatedClientNoFromList(list, { name: newClientName, email: newClientEmail });
        } catch (_) {
          createdClientNo = null;
        }
      }

      if (createdClientNo != null) {
        try {
          await createClientStaff({
            clientNo: createdClientNo,
            loginId: newClientEmail,
            nameKr: newClientOwner,
            phone: newClientPhone,
          });
        } catch (e) {
          try {
            window.alert(`거래처는 생성되었으나 직원 자동 생성에 실패했습니다.\n${e?.message ? String(e.message) : ''}`);
          } catch (_) {
          }
        }
      }

      setIsCreateModalOpen(false);
      await fetchData();
    } catch (e) {
      setCreateError('거래처를 추가하지 못했습니다.');
    } finally {
      setCreating(false);
    }
  };

  const filteredRows = useMemo(() => {
    return rows.filter(r => {
      const nameOk = filterName ? String(r?.거래처명 || '').toLowerCase().includes(filterName.toLowerCase()) : true;
      const mgrOk = filterManager ? String(r?.담당자명 || '').toLowerCase().includes(filterManager.toLowerCase()) : true;
      const phoneOk = filterPhone ? String(r?.대표전화 || '').toLowerCase().includes(filterPhone.toLowerCase()) : true;
      const emailOk = filterEmail ? String(r?.대표이메일 || '').toLowerCase().includes(filterEmail.toLowerCase()) : true;
      return nameOk && mgrOk && phoneOk && emailOk;
    });
  }, [rows, filterName, filterManager, filterPhone, filterEmail]);

  const displayedRows = useMemo(() => {
    const showActive = activeTab === 'active';
    return filteredRows.filter(r => {
      const isActive = !!r?.사용여부;
      return showActive ? isActive : !isActive;
    });
  }, [filteredRows, activeTab]);

  const pickSampleInfluencer = (idx) => {
    const safeIdx = Number(idx);
    const list = sampleInfluencers;
    if (!Number.isFinite(safeIdx)) return list[0] ?? null;
    if (!Array.isArray(list) || list.length === 0) return null;
    return list[((safeIdx % list.length) + list.length) % list.length];
  };

  return (
    <div className="p-4">
      <div className="mb-3 flex items-center justify-between">
        <h1 className="text-lg font-semibold">인플루언서 목록</h1>
        <button
          type="button"
          className="px-3 py-1.5 text-sm rounded bg-blue-600 text-white hover:bg-blue-700"
          onClick={handleOpenCreateModal}
        >
          신규 거래처 추가
        </button>
      </div>

      <div className="mb-3 grid grid-cols-1 md:grid-cols-4 gap-2">
        <input
          value={filterName}
          onChange={(e) => setFilterName(e.target.value)}
          className="w-full border rounded px-3 py-2 text-sm"
          placeholder="거래처명 검색"
        />
        <input
          value={filterManager}
          onChange={(e) => setFilterManager(e.target.value)}
          className="w-full border rounded px-3 py-2 text-sm"
          placeholder="담당자명 검색"
        />
        <input
          value={filterPhone}
          onChange={(e) => setFilterPhone(e.target.value)}
          className="w-full border rounded px-3 py-2 text-sm"
          placeholder="전화번호 검색"
        />
        <div className="flex gap-2">
          <input
            value={filterEmail}
            onChange={(e) => setFilterEmail(e.target.value)}
            className="w-full border rounded px-3 py-2 text-sm"
            placeholder="이메일 검색"
          />
          <button
            type="button"
            className="px-3 py-2 text-sm rounded border border-gray-300 hover:bg-gray-50 whitespace-nowrap"
            onClick={() => { setFilterName(''); setFilterManager(''); setFilterPhone(''); setFilterEmail(''); }}
          >
            초기화
          </button>
        </div>
      </div>

      {loading && (
        <div className="py-10 text-center text-gray-500">로딩 중...</div>
      )}
      {!loading && error && (
        <div className="py-10 text-center text-red-600">{error}</div>
      )}
      {!loading && !error && (
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="min-w-0 flex-1">
          <div className="mb-2 flex gap-2">
            <button
              type="button"
              className={`px-3 py-1.5 text-sm rounded border ${activeTab === 'active' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
              onClick={() => setActiveTab('active')}
            >
              활성 거래처
            </button>
            <button
              type="button"
              className={`px-3 py-1.5 text-sm rounded border ${activeTab === 'inactive' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
              onClick={() => setActiveTab('inactive')}
            >
              비활성 거래처
            </button>
          </div>
          <div className="overflow-auto border border-gray-200 rounded-md">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">거래처명</th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">대표자명</th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">대표이메일</th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">대표전화</th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">사업장주소</th>
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">사용여부</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {displayedRows.map((r, idx) => (
                <tr
                  key={idx}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => setSelectedInfluencer(pickSampleInfluencer(idx))}
                  onDoubleClick={() => handleOpenEditModal(r)}
                >
                  <td className="px-4 py-4 text-sm text-gray-900 whitespace-nowrap">{r?.거래처명 ?? ''}</td>
                  <td className="px-4 py-4 text-sm text-gray-700 whitespace-nowrap">{r?.대표자명 ?? ''}</td>
                  <td className="px-4 py-4 text-sm text-gray-700 whitespace-nowrap">{r?.대표이메일 ?? ''}</td>
                  <td className="px-4 py-4 text-sm text-gray-700 whitespace-nowrap">{r?.대표전화 ?? ''}</td>
                  <td className="px-4 py-4 text-sm text-gray-700">{r?.사업장주소 ?? ''}</td>
                  <td className="px-4 py-4 text-sm text-gray-700 whitespace-nowrap">{r?.사용여부 ? '활성' : '비활성'}</td>
                </tr>
              ))}
              {displayedRows.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-center text-sm text-gray-500">표시할 데이터가 없습니다.</td>
                </tr>
              )}
            </tbody>
          </table>
          </div>
          </div>

          <div className="w-full lg:w-96 shrink-0">
            <div className="border border-gray-200 rounded-md bg-white p-4">
              <div className="text-sm font-semibold text-gray-900">인플루언서 프로필</div>
              {!selectedInfluencer && (
                <div className="mt-3 text-sm text-gray-500">목록에서 인플루언서를 선택하면 샘플 프로필이 표시됩니다.</div>
              )}
              {selectedInfluencer && (
                <div className="mt-3 space-y-3">
                  <div>
                    <div className="text-base font-semibold text-gray-900">{selectedInfluencer.name}</div>
                    <div className="text-sm text-gray-600">{selectedInfluencer.handle}</div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="rounded bg-gray-50 p-2">
                      <div className="text-xs text-gray-500">활동 지역</div>
                      <div className="text-sm text-gray-900">{selectedInfluencer.location}</div>
                    </div>
                    <div className="rounded bg-gray-50 p-2">
                      <div className="text-xs text-gray-500">플랫폼</div>
                      <div className="text-sm text-gray-900">{(selectedInfluencer.platforms || []).join(', ')}</div>
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">카테고리</div>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {(selectedInfluencer.categories || []).map((c) => (
                        <span key={c} className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-100">{c}</span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">팔로워</div>
                    <div className="mt-1 text-sm text-gray-900">
                      {Object.entries(selectedInfluencer.followers || {}).map(([k, v]) => `${k}: ${Number(v || 0).toLocaleString()}`).join(' / ')}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">소개</div>
                    <div className="mt-1 text-sm text-gray-700 whitespace-pre-wrap">{selectedInfluencer.bio}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">연락처</div>
                    <div className="mt-1 text-sm text-gray-900">{selectedInfluencer.contactEmail}</div>
                  </div>
                  <div className="pt-2 flex justify-end">
                    <button
                      type="button"
                      className="px-3 py-1.5 text-sm rounded border border-gray-300 hover:bg-gray-50"
                      onClick={() => setSelectedInfluencer(null)}
                    >
                      선택 해제
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {isCreateModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-4">
            <h2 className="text-base font-semibold mb-3">{isEditMode ? '거래처 편집' : '신규 거래처 추가'}</h2>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">거래처명</label>
                <input
                  className="w-full border rounded px-3 py-2 text-sm"
                  value={newClientName}
                  onChange={(e) => setNewClientName(e.target.value)}
                  placeholder="거래처명을 입력하세요"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">대표 이메일</label>
                <input
                  className="w-full border rounded px-3 py-2 text-sm"
                  value={newClientEmail}
                  onChange={(e) => setNewClientEmail(e.target.value)}
                  placeholder="대표 이메일을 입력하세요"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">대표자</label>
                <input
                  className="w-full border rounded px-3 py-2 text-sm"
                  value={newClientOwner}
                  onChange={(e) => setNewClientOwner(e.target.value)}
                  placeholder="대표자명을 입력하세요"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">대표전화</label>
                <input
                  className="w-full border rounded px-3 py-2 text-sm"
                  value={newClientPhone}
                  onChange={(e) => setNewClientPhone(e.target.value)}
                  placeholder="대표전화를 입력하세요"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">주소</label>
                <input
                  className="w-full border rounded px-3 py-2 text-sm"
                  value={newClientAddress}
                  onChange={(e) => setNewClientAddress(e.target.value)}
                  placeholder="주소를 입력하세요"
                />
              </div>
              {isEditMode && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">사용여부</label>
                  <div className="flex items-center gap-4 text-sm text-gray-700">
                    <label className="inline-flex items-center">
                      <input
                        type="radio"
                        name="client-active"
                        className="mr-2"
                        checked={!!newClientActive}
                        onChange={() => setNewClientActive(true)}
                      />
                      활성
                    </label>
                    <label className="inline-flex items-center">
                      <input
                        type="radio"
                        name="client-active"
                        className="mr-2"
                        checked={!newClientActive}
                        onChange={() => setNewClientActive(false)}
                      />
                      비활성
                    </label>
                  </div>
                </div>
              )}
              {createError && (
                <div className="text-xs text-red-600">{createError}</div>
              )}
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                className="px-3 py-1.5 text-sm rounded border border-gray-300 hover:bg-gray-50"
                onClick={handleCloseCreateModal}
                disabled={creating}
              >
                취소
              </button>
              <button
                type="button"
                className="px-3 py-1.5 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
                onClick={isEditMode ? () => handleUpdateClient(newClientActive) : handleCreateClient}
                disabled={creating}
              >
                {creating ? '저장 중...' : '저장'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientManagement;
